/**
 * Tabular Data Type Detector
 *
 * Detects CSV, TSV, and pipe-delimited data using shared format detection.
 * Priority 35 — below Markdown (40) to avoid misclassifying Markdown tables.
 *
 * @see src/lib/tabularFormatDetection.ts
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';
import { detectTabularFormat } from '@/lib/tabularFormatDetection';

/** Maps delimiter names to semantic types. Semicolon maps to CSV (closest match). */
const DELIMITER_TO_TYPE: Record<string, ISemanticType> = {
  comma: SEMANTIC_TYPES.CSV,
  tab: SEMANTIC_TYPES.TSV,
  pipe: SEMANTIC_TYPES.PIPE,
  semicolon: SEMANTIC_TYPES.CSV,
};

export const TabularDetector: ITypeDetector = {
  id: 'tabular-detector',
  name: 'Tabular Data Detector',
  priority: 35,
  detectedType: SEMANTIC_TYPES.CSV, // default; detect() returns the actual type

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    if (trimmed.length < 3) return null;

    // Extra guard: reject if it looks like YAML key-value
    if (/^[\w-]+:\s+/m.test(trimmed) && !trimmed.includes(',') && !trimmed.includes('\t')) {
      return null;
    }

    const info = detectTabularFormat(input);
    if (!info) return null;

    // Require reasonable confidence
    if (info.confidence < 20) return null;

    return DELIMITER_TO_TYPE[info.delimiterName] ?? null;
  },
};
